import React, { useState } from "react";
import { motion } from "framer-motion";
import { Cpu, Network, Cloud, Wrench, Bell, Eye } from "lucide-react";
import { SOLUTIONS } from "../data/content";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const ICONS = {
  termal: Eye,
  ai: Cpu,
  mudahale: Bell,
  bakim: Wrench,
  entegrasyon: Network,
  bulut: Cloud,
};

export default function Solutions() {
  const [active, setActive] = useState(SOLUTIONS[0].id);
  const current = SOLUTIONS.find((s) => s.id === active);
  const CurrentIcon = ICONS[current.id];

  return (
    <div data-testid="page-solutions" className="relative">
      <div className="absolute inset-x-0 top-0 h-[400px] bg-grid opacity-30 pointer-events-none" />
      <div
        className="absolute inset-x-0 top-0 h-[500px] pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(ellipse at 30% 0%, rgba(255,90,0,0.18), transparent 60%)",
        }}
      />

      <section className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-4">
          / Çözümler — 06 Modül
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
          Akıllı termal algılama, <span className="thermal-text">uçtan uca</span> entegrasyon.
        </h1>
        <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">
          Çift spektrum kamera, yapay zeka analizi ve mevcut otomasyon altyapınızla sorunsuz
          entegrasyon — tek bir komuta sistemi altında.
        </p>
      </section>

      {/* Tab system */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 mt-16 md:mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sticky side nav */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-px bg-white/5 border border-white/10">
              {SOLUTIONS.map((s) => {
                const Icon = ICONS[s.id];
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActive(s.id)}
                    data-testid={`solution-tab-${s.id}`}
                    className={`w-full text-left p-5 flex items-start gap-4 transition-all ${
                      isActive
                        ? "bg-[#0D131F] border-l-2 border-l-thermal-orange"
                        : "bg-[#06090F] hover:bg-[#0D131F]/60 border-l-2 border-l-transparent"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 mt-0.5 shrink-0 ${
                        isActive ? "text-thermal-orange" : "text-slate-400"
                      }`}
                      strokeWidth={1.5}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-slate-500">/ {s.code}</span>
                      </div>
                      <div
                        className={`font-semibold mt-1 ${
                          isActive ? "text-white" : "text-slate-200"
                        }`}
                      >
                        {s.title}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.sub}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail readout */}
          <div className="lg:col-span-8">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="terminal-border p-7 md:p-10 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-thermal-orange to-transparent" />
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-2">
                    / Modül {current.code} — {current.sub}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                    {current.title}
                  </h2>
                </div>
                <CurrentIcon className="w-10 h-10 text-thermal-orange shrink-0" strokeWidth={1.5} />
              </div>

              <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                {current.body}
              </p>

              <div className="mt-8 flex flex-wrap gap-2">
                {current.tags.map((t) => (
                  <span
                    key={t}
                    className="font-mono text-[11px] uppercase tracking-[0.18em] border border-thermal-orange/30 text-thermal-orange px-3 py-1.5"
                  >
                    {t}
                  </span>
                ))}
              </div>

              {/* Specs grid */}
              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10">
                {[
                  { k: "Uptime", v: "99.9%" },
                  { k: "Bildirim", v: "<3sn" },
                  { k: "Tespit", v: "AI+ML" },
                  { k: "Erişim", v: "7/24" },
                ].map((spec) => (
                  <div key={spec.k} className="bg-[#06090F] p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      / {spec.k}
                    </div>
                    <div className="font-mono text-xl text-thermal-yellow mt-1">{spec.v}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Integration protocols */}
            <div className="mt-12 terminal-border p-7 md:p-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-3">
                / Entegrasyon Protokolleri
              </div>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">
                Endüstri standardı, sorunsuz haberleşme
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["Modbus TCP/IP", "OPC UA", "MQTT", "REST API", "PLC", "SCADA", "BMS", "ERP"].map(
                  (p) => (
                    <div
                      key={p}
                      className="font-mono text-xs uppercase tracking-[0.15em] border border-white/15 hover:border-thermal-orange hover:text-thermal-orange transition-colors text-slate-300 px-4 py-3 text-center"
                    >
                      {p}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-4">
          / Ölçülebilir Kazanımlar
        </div>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
          Maliyet azalır, verimlilik artar, ömür uzar.
        </h2>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {[
            { v: "%70-85", l: "Plansız duruşlarda azalma" },
            { v: "%40", l: "Bakım maliyetlerinde tasarruf" },
            { v: "%30-50", l: "Ekipman ömründe artış" },
            { v: "%15-25", l: "Enerji verimliliği iyileşmesi" },
          ].map((b, i) => (
            <div key={i} className="bg-[#06090F] p-7" data-testid={`benefit-${i}`}>
              <div className="font-mono text-4xl md:text-5xl text-thermal-orange font-semibold tracking-tighter">
                {b.v}
              </div>
              <div className="mt-3 text-sm text-slate-300">{b.l}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-white/10 pt-10">
          <p className="text-slate-400 max-w-2xl">
            Çözümler bir araya geldiğinde, tesisiniz proaktif bir komuta merkezine dönüşür.
          </p>
          <Link
            to="/sektorler"
            className="inline-flex items-center gap-2 bg-thermal-orange hover:bg-thermal-red text-white px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
            data-testid="solutions-to-sectors"
          >
            Sektör Uygulamaları <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
